/**
 * Types that make a missing translation a build error rather than a bug
 * somebody finds on screen.
 *
 * `en` is the base dictionary and the only place a key is born. Every other
 * dictionary is typed as `Messages`, which is derived from `en`: a missing key
 * fails to compile, an unknown key fails to compile, and a plural entry
 * written as a plain string fails to compile.
 */

import type { en } from './en';

/** Plural categories as `Intl.PluralRules` reports them. `other` always exists. */
export type PluralForms = { other: string } & Partial<Record<Intl.LDMLPluralRule, string>>;

export type MessageKey = keyof typeof en;

/** Keys whose base entry is a single string. */
export type SimpleKey = {
  [K in MessageKey]: (typeof en)[K] extends string ? K : never;
}[MessageKey];

/** Keys whose base entry is a set of plural forms. */
export type PluralKey = Exclude<MessageKey, SimpleKey>;

/** The shape every dictionary must satisfy. */
export type Messages = {
  [K in MessageKey]: (typeof en)[K] extends string ? string : PluralForms;
};

/** Values substituted into `{placeholders}`. Numbers are formatted for the locale. */
export type MessageParams = Record<string, string | number>;

export const LOCALES = ['en', 'ru'] as const;
export type Locale = (typeof LOCALES)[number];

export function isLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value);
}
